import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { WidgetContext } from 'Pages/Widget/Widget.constants';
import { WidgetState } from 'Pages/Widget/Widget.store';
import { RecurrenceType } from 'Pages/Widget/Widget.types';
import { useContext, useMemo } from 'react';
import { AppointmentType, isAppointmentTypeOneTime } from 'shared/recurring';
import { selectEnableAnytimeTimeRangeSelection, selectHasTimeRangeSelection } from 'store/availableTimeRange/selectors';
import { TimeRanges } from 'store/availableTimeRange/types';
import { useGetPriceQuoteQuery } from 'store/widget/widget.api';
import { PriceQuoteRequest, PriceQuoteResponse } from 'store/widget/widget.types';
import { useAppSelector } from './hooks';
import { useGetSelectedAppointment } from './widget';

export interface WidgetQuoteSelection {
  selectedVariantId: number | undefined;
  selectedAddonIds: readonly number[];
  availableAddonIds: readonly number[];
  selectedAppointmentType: AppointmentType | undefined;
  selectedRecurrenceType: RecurrenceType | undefined;
  selectedTimeRange: TimeRanges | undefined;
  hasTimeRangeSelection: boolean;
  enableAnytimeTimeRange: boolean;
}

export type WidgetQuoteState = Pick<
  WidgetState,
  'selectedAddonIds' | 'selectedAppointmentType' | 'selectedRecurrenceType' | 'selectedTimeRange' | 'selectedVariantId'
>;

export interface WidgetTimeRangeSettings {
  hasTimeRangeSelection: boolean;
  enableAnytimeTimeRange: boolean;
}

export interface WidgetQuoteQueryResult<E> {
  currentData?: PriceQuoteResponse;
  isFetching: boolean;
  isError: boolean;
  error?: E;
}

export function toWidgetQuoteSelection(
  {
    selectedAddonIds,
    selectedAppointmentType,
    selectedRecurrenceType,
    selectedTimeRange,
    selectedVariantId,
  }: WidgetQuoteState,
  appointmentAddons: readonly { id: number }[] | undefined,
  { hasTimeRangeSelection, enableAnytimeTimeRange }: WidgetTimeRangeSettings,
): WidgetQuoteSelection {
  return {
    selectedVariantId,
    selectedAddonIds,
    availableAddonIds: (appointmentAddons ?? []).map(({ id }) => id),
    selectedAppointmentType,
    selectedRecurrenceType,
    selectedTimeRange,
    hasTimeRangeSelection,
    enableAnytimeTimeRange,
  };
}

export function buildWidgetQuoteArgs({
  selectedVariantId,
  selectedAddonIds,
  availableAddonIds,
  selectedAppointmentType,
  selectedRecurrenceType,
  selectedTimeRange,
  hasTimeRangeSelection,
  enableAnytimeTimeRange,
}: WidgetQuoteSelection): PriceQuoteRequest | null {
  if (!selectedVariantId) {
    return null;
  }

  const offeredAddonIds = new Set(availableAddonIds);
  const addonIds = [...new Set(selectedAddonIds)]
    .filter((addonId) => offeredAddonIds.has(addonId))
    .sort((a, b) => a - b);
  const recurrenceType =
    isAppointmentTypeOneTime(selectedAppointmentType) || !selectedRecurrenceType ? 'one-time' : selectedRecurrenceType;
  const timeRange =
    hasTimeRangeSelection && selectedTimeRange && (selectedTimeRange !== TimeRanges.Anytime || enableAnytimeTimeRange)
      ? selectedTimeRange
      : undefined;

  return {
    variantId: selectedVariantId,
    addonIds,
    recurrenceType,
    ...(timeRange ? { timeRange } : {}),
  };
}

export function toPriceQuoteQueryArg(args: PriceQuoteRequest | null) {
  return args ?? skipToken;
}

export function toWidgetQuoteResult<E>(
  args: PriceQuoteRequest | null,
  { currentData, isFetching, isError, error }: WidgetQuoteQueryResult<E>,
) {
  return { args, quote: currentData, isFetching, isError, error };
}

export const useWidgetPriceQuote = () => {
  const { state } = useContext(WidgetContext);
  const { selectedAddonIds, selectedAppointmentType, selectedRecurrenceType, selectedTimeRange, selectedVariantId } =
    state;
  const appointmentAddons = useGetSelectedAppointment()?.addons;
  const hasTimeRangeSelection = useAppSelector(selectHasTimeRangeSelection);
  const enableAnytimeTimeRange = useAppSelector(selectEnableAnytimeTimeRangeSelection);

  const args = useMemo(
    () =>
      buildWidgetQuoteArgs(
        toWidgetQuoteSelection(
          { selectedAddonIds, selectedAppointmentType, selectedRecurrenceType, selectedTimeRange, selectedVariantId },
          appointmentAddons,
          { hasTimeRangeSelection, enableAnytimeTimeRange },
        ),
      ),
    [
      appointmentAddons,
      enableAnytimeTimeRange,
      hasTimeRangeSelection,
      selectedAddonIds,
      selectedAppointmentType,
      selectedRecurrenceType,
      selectedTimeRange,
      selectedVariantId,
    ],
  );

  return toWidgetQuoteResult(args, useGetPriceQuoteQuery(toPriceQuoteQueryArg(args)));
};
